import type { IPeriodicalPaymentsApplicationForm } from "@/modules/application/common/types";
import { periodicalPaymentsApplicationValidationSchema } from "@/modules/application/validations";
import { CloudflareAsset, SideSheetPosition, StaticUrl } from "@/shared/common/enums";
import type { IOperationPageProps } from "@/shared/common/types";
import { CloudFlareImage, InfoLinks, PageHeader } from "@/shared/components";
import { useMediaQuery, useTranslationExists } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useState } from "react";

import { PeriodicalPaymentsApplicationForm } from "./PeriodicalPaymentsApplicationForm";

export const PeriodicalPaymentsApplication = ({ isEdit, isCopy }: IOperationPageProps) => {
  const { t, exists } = useTranslationExists();
  const isDesktop = useMediaQuery();
  const [isSideBarOpen, setSideBarOpen] = useState<boolean>(false);
  const [isAccountNumberInvalid, setIsAccountNumberInvalid] = useState<boolean>(false);

  const initialValues: IPeriodicalPaymentsApplicationForm = {
    debitAccount: "",
    email: "",
    amount: "",
    creditAccount: "",
    beneficiary: "",
    beneficiaryBank: "",
    frequencyInfo: "",
    startDate: "",
    endDate: "",
    sendEmailNotification: false,
    workingDayAdjustment: "",
    applicationNumber: "",
    isAgreeWithConditions: false,
    purpose: t("application.periodicalPayments.purpose"),
  };

  return (
    <>
      <FormContainer
        className="full-width page-container form-layout"
        formId="form-container"
        mode="onBlur"
        reValidateMode="onBlur"
        initialValues={initialValues}
        validationScheme={periodicalPaymentsApplicationValidationSchema({
          t, isAccountNumberInvalid
        })}
      >
        <PageHeader>
          {!isSideBarOpen && exists("application.sidebarTitle") && (
            <Button
              type="secondary"
              size="small"
              className="side-sheet-btn"
              data-testid="open-info-side-sheet"
              data-ga-id="open-info-side-sheet"
              data-ga-action="Open Periodical payments applicatio info side sheet"
              iconProps={{
                Component: (props: ISVGIconProps) => (
                  <CloudFlareImage {...props} path={CloudflareAsset.Info}/>
                ),
                alignment: "left",
              }}
              onClick={() => setSideBarOpen(!isSideBarOpen)}
            />
          )}
        </PageHeader>
        <PeriodicalPaymentsApplicationForm
          isEdit={isEdit}
          isCopy={isCopy}
          setIsAccountNumberInvalid={setIsAccountNumberInvalid}
        />
      </FormContainer>
      <SideSheet
        position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
        isPositioned={!isDesktop}
        withOverlay={!isDesktop}
        isOpen={isSideBarOpen}
        onClose={() => setSideBarOpen(false)}
        title={t("application.sidebarTitle")}
      >
        <div className="flexbox flex-wrap--wrap">
          <Text type="primary" className="mb-10">
            {t("application.periodicalPayments.sidebarMessage")}
          </Text>
          <InfoLinks
            keys={[
              StaticUrl.CorporateTariffs,
              StaticUrl.CardTariffs,
              StaticUrl.TermsAndConditions,
            ]}
          />
        </div>
      </SideSheet>
    </>
  );
};
